import { useEffect, useState } from "react";
import { useDebouncedEffect } from "../hooks";
import { validateEmail } from "../utils";

type UseValidatedTextField = {
    value: string;
    setValue: (value: string) => void;
    errorMessage: string;
};

/**
 * Used in text field components which contain optional required fields and regex validation.
 *
 * @param reduxValue - Value in the actual Redux state
 * @param setReduxValue - Set a new validated value in Redux state
 * @param validationConfig - Variables related to validation logic
 */
const useValidatedTextField = (
    reduxValue: string,
    setReduxValue: (value: string) => void,
    validationConfig: {
        showRequiredError: boolean; // Show the required check
        requiredLabel: string; // Required label shown when showRequiredError
        required?: boolean; // True when the TextField is required
        validationRule?: string; // String version of regex check
        validationError?: string; // Label shown when the validationRule fails
        hardValidationRule?: RegExp; // Regex check, input will not allow any other values to be typed.
        isEmail?: boolean; // Indicates if the text field is an email type
        emailValidationError?: string; // Email validation error label
    },
): UseValidatedTextField => {
    const { showRequiredError, requiredLabel, required, validationError, validationRule } = validationConfig;
    const { isEmail, emailValidationError, hardValidationRule } = validationConfig;
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [value, setValue] = useState<string>(reduxValue as string);

    // The current implementation propagates valid input to Redux.
    // This means validation happens inside the component vs in redux-logic or other related functions.
    // It is possible this approach becomes too complicated or wont fit requirements in which case we might need to rework this.
    useDebouncedEffect(
        () => {
            let propagatedValue = "";
            let newErrorMessage = "";

            if (value) {
                // If we have a value, validate it and propagate to redux if valid.
                // Check email validation first if relevant.
                if (isEmail && !validateEmail(value)) {
                    newErrorMessage = emailValidationError || "";
                    propagatedValue = "";
                } else if (validationRule) {
                    const isValid = !!new RegExp(validationRule).exec(value)?.[0];
                    if (isValid) {
                        propagatedValue = value;
                    } else {
                        newErrorMessage = validationError || "";
                        propagatedValue = "";
                    }
                } else {
                    propagatedValue = value;
                }
            }

            // Update relevant states if required.
            if (propagatedValue !== reduxValue) {
                if (hardValidationRule) propagatedValue = hardValidate(propagatedValue, hardValidationRule);
                setReduxValue(propagatedValue);
            }
            if (errorMessage !== newErrorMessage) setErrorMessage(newErrorMessage);
        },
        500, // Debounce to avoid immediate updates during typing.
        [value],
    );

    // Any unmatched values of the string according to the provided regex will be removed.
    const hardValidate = (stringToConcat: string | undefined, regex: RegExp): string => {
        return stringToConcat ? stringToConcat.replace(regex, "") : "";
    };

    // Required error logic. This is specifically separated from the hook above to get an immediate update in frontend when executing validation.
    useEffect(() => {
        if (!value && showRequiredError && required) setErrorMessage(requiredLabel);
    }, [value, showRequiredError, required, requiredLabel]);

    return { errorMessage, value: hardValidationRule ? hardValidate(value, hardValidationRule) : value, setValue };
};

export default useValidatedTextField;
